import React, { useState } from "react";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import { TESTIDS } from "@/constants/testIds";
import { ArrowRight, ArrowLeft, X, CheckCircle2 } from "lucide-react";
import { useWorkspaceTranslation } from "@/locales/workspaceExtras";

const STORAGE_KEY = "td_onboarded";

const WORK_AREAS = [
  { key: "commodity",   labels: { en: "Commodity Trading",   it: "Commodity Trading",   de: "Commodity Trading" },   testid: TESTIDS.onbAreaCommodity },
  { key: "shipping",    labels: { en: "Shipping",            it: "Shipping",            de: "Schifffahrt" },          testid: TESTIDS.onbAreaShipping },
  { key: "chartering",  labels: { en: "Chartering",          it: "Chartering",          de: "Chartering" },           testid: TESTIDS.onbAreaChartering },
  { key: "tradefin",    labels: { en: "Trade Finance",       it: "Trade Finance",       de: "Trade Finance" },        testid: TESTIDS.onbAreaTradeFin },
  { key: "importexport",labels: { en: "Import & Export",     it: "Import ed Export",    de: "Import & Export" },      testid: TESTIDS.onbAreaImportExport },
  { key: "corporate",   labels: { en: "Corporate Documents", it: "Documenti aziendali", de: "Corporate-Dokumente" },  testid: TESTIDS.onbAreaCorporate },
  { key: "other",       labels: { en: "Other",               it: "Altro",               de: "Sonstiges" },            testid: TESTIDS.onbAreaOther },
];

const COPY = {
  en: {
    kicker: "First-time set-up", skip: "Skip for now", close: "Close", back: "Back", next: "Continue", finish: "Enter the workspace",
    step1_title: "Welcome to TradeDocs AI Desk.",
    step1_sub: "An AI-assisted document workspace for international trade, shipping and trade-finance. Let's spend one minute tailoring it to your work.",
    step2_title: "Tell us about your company.",
    step2_sub: "We use this to pre-fill headers, signatories and counterparty templates.",
    step3_title: "Which areas do you work in?",
    step3_sub: "Select all that apply — you can change this later.",
    step4_title: "Preferred document languages.",
    step4_sub: "Interface stays in your chosen language; documents can be generated in any of these.",
    step5_title: "You're all set.",
    step5_sub: "Where would you like to start?",
    company_name: "Legal company name", company_country: "Country",
    business_type: "Business type", primary_activity: "Primary activity",
    biz_ind: "Individual professional", biz_org: "Organisation", biz_startup: "Start-up", biz_sme: "SME", biz_large: "Large enterprise",
    start_deal: "Start a new Deal", start_doc: "Create a Document", start_tpl: "Browse Templates",
  },
  it: {
    kicker: "Configurazione iniziale", skip: "Salta per ora", close: "Chiudi", back: "Indietro", next: "Continua", finish: "Entra nello spazio di lavoro",
    step1_title: "Benvenuto in TradeDocs AI Desk.",
    step1_sub: "Uno spazio documentale assistito da AI per commercio internazionale, shipping e trade finance. Dedichiamo un minuto a configurarlo.",
    step2_title: "Parlaci della tua azienda.",
    step2_sub: "Usiamo questi dati per precompilare intestazioni, firmatari e modelli di controparte.",
    step3_title: "In quali aree operi?",
    step3_sub: "Seleziona tutte quelle applicabili — potrai modificarle in seguito.",
    step4_title: "Lingue documentali preferite.",
    step4_sub: "L'interfaccia resta nella lingua scelta; i documenti possono essere generati in una qualsiasi di queste.",
    step5_title: "Tutto pronto.",
    step5_sub: "Da dove vuoi partire?",
    company_name: "Ragione sociale", company_country: "Paese",
    business_type: "Tipo di attività", primary_activity: "Attività principale",
    biz_ind: "Professionista", biz_org: "Organizzazione", biz_startup: "Start-up", biz_sme: "PMI", biz_large: "Grande impresa",
    start_deal: "Nuova trattativa", start_doc: "Crea un documento", start_tpl: "Sfoglia i modelli",
  },
  de: {
    kicker: "Erst-Einrichtung", skip: "Später einrichten", close: "Schließen", back: "Zurück", next: "Weiter", finish: "Zum Workspace",
    step1_title: "Willkommen bei TradeDocs AI Desk.",
    step1_sub: "Ein KI-gestützter Dokumenten-Workspace für internationalen Handel, Schifffahrt und Trade Finance. Nehmen wir uns eine Minute zum Einrichten.",
    step2_title: "Ihr Unternehmen.",
    step2_sub: "Diese Angaben füllen Briefkopf, Unterzeichner und Gegenpartei-Vorlagen automatisch.",
    step3_title: "In welchen Bereichen arbeiten Sie?",
    step3_sub: "Mehrfachauswahl möglich — später änderbar.",
    step4_title: "Bevorzugte Dokumentsprachen.",
    step4_sub: "Die Oberfläche bleibt in Ihrer gewählten Sprache; Dokumente können in jeder davon erzeugt werden.",
    step5_title: "Alles bereit.",
    step5_sub: "Wo möchten Sie starten?",
    company_name: "Firmenname (rechtlich)", company_country: "Land",
    business_type: "Geschäftsart", primary_activity: "Haupttätigkeit",
    biz_ind: "Einzelperson", biz_org: "Organisation", biz_startup: "Start-up", biz_sme: "KMU", biz_large: "Großunternehmen",
    start_deal: "Neuen Deal starten", start_doc: "Dokument erstellen", start_tpl: "Vorlagen ansehen",
  },
};

function shouldShow(user) {
  if (!user) return false;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return true;
    const done = JSON.parse(raw);
    return !done?.[user.id];
  } catch (e) { return true; }
}

function markDone(userId) {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const done = raw ? JSON.parse(raw) : {};
    done[userId] = { done_at: new Date().toISOString() };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(done));
  } catch (e) { /* no-op */ }
}

export default function OnboardingWizard({ onFinish }) {
  const { user } = useAuth();
  const { lang } = useI18n();
  const { tr } = useWorkspaceTranslation();
  const [visible, setVisible] = useState(() => shouldShow(user));
  const [step, setStep] = useState(0);
  const [data, setData] = useState({
    company_name: user?.company || "",
    country: "",
    business_type: "biz_org",
    primary_activity: "",
    areas: [],
    doc_langs: ["en"],
  });

  if (!visible || !user) return null;
  const copy = Object.fromEntries(Object.entries(COPY[lang] || COPY.en).map(([key, value]) => [key, tr(value)]));

  const toggleArea = (k) => setData((s) => ({
    ...s,
    areas: s.areas.includes(k) ? s.areas.filter((x) => x !== k) : [...s.areas, k],
  }));
  const toggleDocLang = (k) => setData((s) => ({
    ...s,
    doc_langs: s.doc_langs.includes(k) ? s.doc_langs.filter((x) => x !== k) : [...s.doc_langs, k],
  }));

  const close = () => { markDone(user.id); setVisible(false); onFinish && onFinish(); };
  const dismiss = () => { markDone(user.id); setVisible(false); };

  const steps = [
    // 1 — Welcome
    (
      <div className="text-center">
        <div className="w-16 h-16 mx-auto mb-6 flex items-center justify-center"
             style={{ background: "rgba(212,175,55,0.14)", border: "1px solid rgba(212,175,55,0.4)", borderRadius: "50%" }}>
          <div className="w-4 h-4 bg-[var(--td-gold)] rotate-45" />
        </div>
        <h2 className="font-serif text-3xl md:text-4xl mb-4 leading-tight">{copy.step1_title}</h2>
        <p className="text-[var(--td-text-2)] leading-relaxed max-w-lg mx-auto">{copy.step1_sub}</p>
      </div>
    ),
    // 2 — Company profile basics
    (
      <div>
        <h2 className="font-serif text-3xl mb-2 leading-tight">{copy.step2_title}</h2>
        <p className="text-[var(--td-text-2)] leading-relaxed mb-6">{copy.step2_sub}</p>
        <div className="grid md:grid-cols-2 gap-5">
          <div>
            <label className="td-label">{copy.company_name}</label>
            <input data-testid={TESTIDS.onbCompanyName} className="td-input" value={data.company_name}
                   onChange={(e) => setData({ ...data, company_name: e.target.value })} />
          </div>
          <div>
            <label className="td-label">{copy.company_country}</label>
            <input data-testid={TESTIDS.onbCompanyCountry} className="td-input" value={data.country}
                   onChange={(e) => setData({ ...data, country: e.target.value })} />
          </div>
          <div>
            <label className="td-label">{copy.business_type}</label>
            <select data-testid={TESTIDS.onbBusinessType} className="td-input" value={data.business_type}
                    onChange={(e) => setData({ ...data, business_type: e.target.value })}>
              <option value="biz_ind">{copy.biz_ind}</option>
              <option value="biz_org">{copy.biz_org}</option>
              <option value="biz_startup">{copy.biz_startup}</option>
              <option value="biz_sme">{copy.biz_sme}</option>
              <option value="biz_large">{copy.biz_large}</option>
            </select>
          </div>
          <div>
            <label className="td-label">{copy.primary_activity}</label>
            <input data-testid={TESTIDS.onbPrimaryActivity} className="td-input" value={data.primary_activity}
                   onChange={(e) => setData({ ...data, primary_activity: e.target.value })} />
          </div>
        </div>
      </div>
    ),
    // 3 — Work areas
    (
      <div>
        <h2 className="font-serif text-3xl mb-2 leading-tight">{copy.step3_title}</h2>
        <p className="text-[var(--td-text-2)] leading-relaxed mb-6">{copy.step3_sub}</p>
        <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
          {WORK_AREAS.map((a) => {
            const active = data.areas.includes(a.key);
            return (
              <button
                key={a.key}
                type="button"
                data-testid={a.testid}
                onClick={() => toggleArea(a.key)}
                className={`p-4 text-left text-sm transition-colors border ${
                  active
                    ? "border-[var(--td-gold)] text-[var(--td-gold)] bg-[rgba(212,175,55,0.06)]"
                    : "border-[var(--td-border)] text-[var(--td-text-2)] hover:text-[var(--td-text)] hover:border-[var(--td-text-3)]"
                }`}
              >
                <span className="block font-serif text-base">{tr(a.labels[lang] || a.labels.en)}</span>
              </button>
            );
          })}
        </div>
      </div>
    ),
    // 4 — Doc languages
    (
      <div>
        <h2 className="font-serif text-3xl mb-2 leading-tight">{copy.step4_title}</h2>
        <p className="text-[var(--td-text-2)] leading-relaxed mb-6">{copy.step4_sub}</p>
        <div className="grid sm:grid-cols-3 gap-3 sm:gap-4">
          {[
            { k: "en", label: "English", testid: TESTIDS.onbLangEn },
            { k: "it", label: "Italiano", testid: TESTIDS.onbLangIt },
            { k: "de", label: "Deutsch", testid: TESTIDS.onbLangDe },
            { k: "es", label: "Spanish" },
            { k: "zh", label: "Chinese" },
            { k: "ru", label: "Russian" },
          ].map((l) => {
            const active = data.doc_langs.includes(l.k);
            return (
              <button
                key={l.k}
                type="button"
                data-testid={l.testid}
                onClick={() => toggleDocLang(l.k)}
                className={`p-4 sm:p-6 text-center border transition-colors ${
                  active ? "border-[var(--td-gold)] bg-[rgba(212,175,55,0.06)]" : "border-[var(--td-border)] hover:border-[var(--td-text-3)]"
                }`}
              >
                <span className="font-mono text-[11px] uppercase tracking-widest text-[var(--td-text-3)] block mb-1">{l.k.toUpperCase()}</span>
                <span className={`font-serif text-lg ${active ? "text-[var(--td-gold)]" : ""}`}>{l.label}</span>
              </button>
            );
          })}
        </div>
      </div>
    ),
    // 5 — First action
    (
      <div className="text-center">
        <CheckCircle2 size={40} className="text-[var(--td-gold)] mx-auto mb-4" />
        <h2 className="font-serif text-3xl md:text-4xl mb-3 leading-tight">{copy.step5_title}</h2>
        <p className="text-[var(--td-text-2)] leading-relaxed mb-8 max-w-md mx-auto">{copy.step5_sub}</p>
        <div className="grid md:grid-cols-3 gap-3 max-w-2xl mx-auto text-left">
          {[
            { k: "deal", label: copy.start_deal, path: "/app/deals" },
            { k: "doc",  label: copy.start_doc,  path: "/app/drafting" },
            { k: "tpl",  label: copy.start_tpl,  path: "/app/templates" },
          ].map((c) => (
            <a
              key={c.k}
              href={c.path}
              onClick={close}
              className="card-surface p-5 hover:border-[var(--td-gold)] transition-colors"
            >
              <div className="font-serif text-base leading-tight">{c.label}</div>
              <ArrowRight size={14} className="text-[var(--td-gold)] mt-3" />
            </a>
          ))}
        </div>
      </div>
    ),
  ];

  const isLast = step === steps.length - 1;

  return (
    <div className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm flex items-start sm:items-center justify-center p-3 sm:p-4 md:p-6 overflow-y-auto">
      <div className="card-surface w-full max-w-3xl p-5 sm:p-8 md:p-10 relative my-3 sm:my-0 max-h-[calc(100dvh-1.5rem)] overflow-y-auto">
        <button
          type="button"
          onClick={dismiss}
          data-testid={TESTIDS.onbSkip}
          aria-label={copy.close}
          title={copy.close}
          className="absolute top-4 right-4 text-[var(--td-text-3)] hover:text-[var(--td-text)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--td-gold)]"
        >
          <X size={18} aria-hidden="true" />
          <span className="sr-only">{copy.close}</span>
        </button>

        <div className="kicker mb-6">— {copy.kicker} · {step + 1} / {steps.length}</div>

        {/* progress bar */}
        <div className="flex gap-1 mb-8">
          {steps.map((_, i) => (
            <div key={i}
                 className="h-1 flex-1 transition-colors"
                 style={{ background: i <= step ? "var(--td-gold)" : "var(--td-border)" }} />
          ))}
        </div>

        <div className="min-h-[280px]">{steps[step]}</div>

        <div className="flex items-center justify-between mt-8">
          <button
            type="button"
            data-testid={TESTIDS.onbBack}
            disabled={step === 0}
            onClick={() => setStep((s) => Math.max(0, s - 1))}
            className="inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-widest text-[var(--td-text-3)] hover:text-[var(--td-text)] disabled:opacity-30"
          >
            <ArrowLeft size={13} /> {copy.back}
          </button>

          {!isLast ? (
            <button
              type="button"
              data-testid={TESTIDS.onbNext}
              onClick={() => setStep((s) => Math.min(steps.length - 1, s + 1))}
              className="btn-gold inline-flex items-center gap-2"
            >
              {copy.next} <ArrowRight size={13} />
            </button>
          ) : (
            <button
              type="button"
              data-testid={TESTIDS.onbFinish}
              onClick={close}
              className="btn-gold inline-flex items-center gap-2"
            >
              {copy.finish} <ArrowRight size={13} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
